import {
   COMMAND_STATUS,
   DEFAULT_COMMAND_MAX_RETRIES,
   DEFAULT_COMMAND_RETRY_DELAY_MS,
   DEFAULT_COMMAND_TIMEOUT_MS,
   createCalendarCommand,
   isIdempotentCommandType,
} from "./commandTypes";
import { createCalendarUndoStack } from "./undoStack";

function sleep(ms) {
   return new Promise((resolve) => {
      setTimeout(resolve, ms);
   });
}

function withTimeout(promise, timeoutMs) {
   let timerId = 0;
   const timeoutPromise = new Promise((_, reject) => {
      timerId = setTimeout(() => {
         reject(new Error("command-timeout"));
      }, timeoutMs);
   });
   return Promise.race([promise, timeoutPromise]).finally(() => {
      clearTimeout(timerId);
   });
}

/**
 * Professional mutation queue for Master Calendar.
 * - serial execution (one command at a time)
 * - per-command timeout
 * - retry with backoff
 * - frozen snapshot at enqueue time (clipboard-safe)
 * - status tracking for UI + diagnostics
 * - undo/redo run through the same serial drain (not concurrent with mutations)
 */
export class CalendarCommandQueue {
   constructor({
      execute,
      onStatusChange,
      maxRetries = DEFAULT_COMMAND_MAX_RETRIES,
      timeoutMs = DEFAULT_COMMAND_TIMEOUT_MS,
      retryDelayMs = DEFAULT_COMMAND_RETRY_DELAY_MS,
      undoStack = null,
   } = {}) {
      if (typeof execute !== "function") {
         throw new Error("CalendarCommandQueue requires execute(fn)");
      }
      this._execute = execute;
      this._onStatusChange =
         typeof onStatusChange === "function" ? onStatusChange : null;
      this._maxRetries = maxRetries;
      this._timeoutMs = timeoutMs;
      this._retryDelayMs = retryDelayMs;
      this._queue = [];
      this._commands = new Map();
      this._draining = false;
      this._undoStack = undoStack || createCalendarUndoStack();
   }

   get undoStack() {
      return this._undoStack;
   }

   canUndo() {
      return this._undoStack.canUndo();
   }

   canRedo() {
      return this._undoStack.canRedo();
   }

   /**
    * Enqueue undo on the serial drain. Stack entry moves to redo only after success.
    */
   undo() {
      return new Promise((resolve, reject) => {
         this._queue.push({ kind: "undo", resolve, reject });
         void this._drain();
      });
   }

   /**
    * Enqueue redo on the serial drain. Stack entry moves to undo only after success.
    */
   redo() {
      return new Promise((resolve, reject) => {
         this._queue.push({ kind: "redo", resolve, reject });
         void this._drain();
      });
   }

   hasPendingWork() {
      if (this._queue.length > 0) return true;
      for (const command of this._commands.values()) {
         if (
            command.status === COMMAND_STATUS.PENDING ||
            command.status === COMMAND_STATUS.RUNNING
         ) {
            return true;
         }
      }
      return false;
   }

   getCommand(commandId) {
      return this._commands.get(String(commandId || "")) || null;
   }

   listCommands() {
      return Array.from(this._commands.values());
   }

   _emit(command) {
      this._onStatusChange?.(command);
   }

   _setStatus(command, status, patch = {}) {
      const next = {
         ...command,
         status,
         ...patch,
      };
      this._commands.set(next.id, next);
      this._emit(next);
      return next;
   }

   enqueue(type, payload = {}, snapshot = null, options = {}) {
      const command = createCalendarCommand({
         type,
         payload,
         snapshot,
         id: options.id,
         maxRetries:
            options.maxRetries != null ? options.maxRetries : this._maxRetries,
         timeoutMs:
            options.timeoutMs != null ? options.timeoutMs : this._timeoutMs,
      });
      this._commands.set(command.id, command);
      this._emit(command);

      return new Promise((resolve, reject) => {
         this._queue.push({ kind: "command", command, resolve, reject });
         void this._drain();
      });
   }

   async _runUndo() {
      const entry = this._undoStack.takeUndo();
      if (!entry || typeof entry.undo !== "function") return false;
      try {
         await entry.undo();
         this._undoStack.confirmUndo(entry);
         return true;
      } catch (error) {
         this._undoStack.restoreUndo(entry);
         throw error;
      }
   }

   async _runRedo() {
      const entry = this._undoStack.takeRedo();
      if (!entry || typeof entry.redo !== "function") return false;
      try {
         await entry.redo();
         this._undoStack.confirmRedo(entry);
         return true;
      } catch (error) {
         this._undoStack.restoreRedo(entry);
         throw error;
      }
   }

   async _runCommand(command) {
      let current = this._setStatus(command, COMMAND_STATUS.RUNNING, {
         startedAt: Date.now(),
         error: null,
      });

      while (current.retryCount <= current.maxRetries) {
         try {
            const result = await withTimeout(
               this._execute(current),
               current.timeoutMs,
            );
            const done = this._setStatus(current, COMMAND_STATUS.SUCCEEDED, {
               completedAt: Date.now(),
               result,
            });
            if (typeof current.payload?.buildUndoEntry === "function") {
               const undoEntry = current.payload.buildUndoEntry(result, current);
               if (undoEntry) this._undoStack.push(undoEntry);
            }
            return done;
         } catch (error) {
            const isTimeout = String(error?.message || error) === "command-timeout";
            // PASTE/CREATE/MOVE must not re-execute after timeout — the original
            // request may still succeed and a retry would duplicate.
            const canRetry =
               !error?.noRetry &&
               current.retryCount < current.maxRetries &&
               isIdempotentCommandType(current.type);
            if (canRetry) {
               current = this._setStatus(current, COMMAND_STATUS.PENDING, {
                  retryCount: current.retryCount + 1,
                  error,
               });
               await sleep(this._retryDelayMs * (current.retryCount + 1));
               current = this._setStatus(current, COMMAND_STATUS.RUNNING, {
                  startedAt: Date.now(),
               });
               continue;
            }

            const failedStatus = isTimeout
               ? COMMAND_STATUS.TIMEOUT
               : COMMAND_STATUS.FAILED;
            const failed = this._setStatus(current, failedStatus, {
               completedAt: Date.now(),
               error,
            });
            throw failed;
         }
      }

      throw current;
   }

   async _drain() {
      if (this._draining) return;
      this._draining = true;
      while (this._queue.length > 0) {
         const job = this._queue.shift();
         if (!job) continue;

         if (job.kind === "undo") {
            try {
               job.resolve(await this._runUndo());
            } catch (error) {
               job.reject(error);
            }
            continue;
         }

         if (job.kind === "redo") {
            try {
               job.resolve(await this._runRedo());
            } catch (error) {
               job.reject(error);
            }
            continue;
         }

         try {
            const done = await this._runCommand(job.command);
            job.resolve(done);
         } catch (error) {
            job.reject(error);
         }
      }
      this._draining = false;
   }

   /** @internal */
   __resetForTests() {
      this._queue.length = 0;
      this._commands.clear();
      this._draining = false;
      this._undoStack.clear();
   }
}

export function createCalendarCommandQueue(options) {
   return new CalendarCommandQueue(options);
}
